import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import SettingsScreen from "./SettingsScreen";
import WaterScreen from "./WaterScreen";
import FlatsScreen from "./FlatsScreen";
import TasksScreen from "./TasksScreen";
import ChatScreen from "./ChatScreen";
import ContactsScreen from "./ContactsScreen";
import PassbookScreen from "./PassbookScreen";
import { TankerOrderModal } from "./WaterScreen";
import PollScreen from "./PollScreen";
import BylawsScreen from "./BylawsScreen";
// import WaterDataImportScreen from "./WaterDataImportScreen";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import adyantamLogo from "../assets/adyantam-logo.png";
import NotificationBell from "./NotificationBell";
import FeedbackWidget from "./FeedbackWidget";
import ReportsScreen from "./ReportsScreen";

// ── Helpers ────────────────────────────────────────────────────
function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// Returns an array of YYYY-MM month keys from the start of the current
// Indian financial year (April) through the current month, inclusive.
// e.g. in June 2026 → ["2026-04", "2026-05", "2026-06"]
function fyMonthKeys() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1-12
  const fyStartYear = m >= 4 ? y : y - 1;

  const months = [];
  let cy = fyStartYear, cm = 4;
  while (true) {
    months.push(`${cy}-${String(cm).padStart(2, "0")}`);
    if (cy === y && cm === m) break;
    cm++;
    if (cm > 12) { cm = 1; cy++; }
  }
  return months;
}

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

// Counts months from the society's own Tracking Start Month (read from
// Settings, e.g. "2026-04" for BSKR) through the given month key,
// inclusive. Unlike fyMonthKeys(), this NEVER resets at a financial year
// boundary — Brought Forward must keep accumulating across years.
// e.g. trackingStartMonth "2026-04", monthKey "2026-07" → 4.
// Falls back to "2026-04" only if trackingStartMonth is missing from
// Settings (should not normally happen once every society has one set).
function monthsSinceTrackingStart(monthKey, settings) {
  const startKey = (settings && settings.trackingStartMonth) || "2026-04";
  const [startYear, startMonth] = startKey.split("-").map(Number);
  const [y, m] = (monthKey || currentMonthKey()).split("-").map(Number);
  return (y - startYear) * 12 + (m - startMonth) + 1;
}

// Works out a flat's correct monthly maintenance rate from Settings,
// based on Occupied/Vacant status. Mirrors the same helper in
// FlatsScreen.jsx — keep both in sync if the rate logic ever changes.
function computeMonthlyRate(flat, settings) {
  if (!settings) return 0;
  const base = Number(settings.maintenanceAmt || 0);
  if (flat?.status === "Vacant") {
    const pct = Number(settings.vacantPct || 0);
    return Math.round(base * (pct / 100));
  }
  return base;
}

// ── Brought Forward calculation (Maintenance) ───────────────────
// This is the core fix: works out Total Due, Total Paid, Total
// Outstanding, and splits it into Brought Forward / This Month —
// per the locked design (session summary, Section 3.2 & 3.5).
export function computeMaintenanceLedger({ flat, payments, maintenanceBills, settings, month }) {
  const flatId = flat?.flatId || flat?.flatNo;
  const openingArrears = Number(flat?.openingArrearsMonths || 0);

  // ── Exception case: a flat with a special manually-set rate switched ON ──
  // Saved per-month bill records don't carry exception rates yet, so for
  // this case only, fall back to the old full-recalculation method.
  // (Not active for any flat today, but kept safe for when it is.)
  if (flat?.exceptionActive) {
    const rate = Number(flat.exceptionAmount || 0);
    const monthsElapsed = monthsSinceTrackingStart(month, settings);
    const totalMonths = openingArrears + monthsElapsed;
    const totalDue = totalMonths * rate;

    const totalPaid = (payments || [])
      .filter(p =>
        p.flatId === flatId &&
        p.status === "confirmed" &&
        p.type && p.type.toLowerCase() === "maintenance"
      )
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

    const totalOutstanding = Math.max(0, totalDue - totalPaid);
    const broughtForward   = Math.max(0, totalOutstanding - rate);
    const presentMonthDue  = Math.min(totalOutstanding, rate);

    return { rate, totalMonths, totalDue, totalPaid, totalOutstanding, broughtForward, presentMonthDue };
  }

  // ── Normal case: read from saved per-month bill records ──────────────
  // Each bill permanently keeps the rate that applied when it was created,
  // so a future rate change never gets wrongly applied to old months.
  const myBills = (maintenanceBills || []).filter(b => b.flatId === flatId);
  const billsDue = myBills.reduce((sum, b) => sum + Number(b.amountDue || 0), 0);

  // Opening arrears (months owed from before tracking started) are still
  // added on top, at today's Settings rate — same treatment as before,
  // not a new behavior.
  const openingArrearsRate = computeMonthlyRate(flat, settings);
  const openingArrearsDue  = openingArrears * openingArrearsRate;

  const totalDue = billsDue + openingArrearsDue;
  const totalMonths = myBills.length + openingArrears;

  const totalPaid = (payments || [])
    .filter(p =>
      p.flatId === flatId &&
      p.status === "confirmed" &&
      p.type && p.type.toLowerCase() === "maintenance"
    )
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const totalOutstanding = Math.max(0, totalDue - totalPaid);

  // This month's own saved due (falls back to live Settings rate only if
  // this month's bill hasn't been created yet)
  const currentBill = myBills.find(b => b.month === month);
  const rate = currentBill ? Number(currentBill.amountDue || 0) : computeMonthlyRate(flat, settings);

  // Oldest-first split: this month's due is cleared last, so whatever
  // outstanding remains ABOVE this month's due is the Brought Forward part.
  const broughtForward  = Math.max(0, totalOutstanding - rate);
  const presentMonthDue  = Math.min(totalOutstanding, rate);

  return {
    rate, totalMonths, totalDue, totalPaid, totalOutstanding,
    broughtForward, presentMonthDue,
  };
}

// Converts a Firestore month key (YYYY-MM) to display format (Mon-YYYY)
// e.g. "2026-06" → "Jun-2026"
function formatMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${monthNames[parseInt(m, 10) - 1]}-${y}`;
}

// Indian financial year runs Apr–Mar. Returns a label like "FY 2026-27 · Apr – Jun 2026"
function fyToDateLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1-12
  const fyStartYear = m >= 4 ? y : y - 1;
  const fyEndYear = fyStartYear + 1;
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `FY ${fyStartYear}-${String(fyEndYear).slice(-2)} · Apr – ${monthNames[m - 1]} ${y}`;
}

// ── Pill / Badge ───────────────────────────────────────────────
function Badge({ text, color }) {
  const colors = {
    green:  { bg: "#4caf5018", border: "#4caf5040", text: "#4caf50" },
    red:    { bg: "#ff525218", border: "#ff525240", text: "#ff5252" },
    amber:  { bg: "#ffa72618", border: "#ffa72640", text: "#ffa726" },
    blue:   { bg: "#42a5f518", border: "#42a5f540", text: "#42a5f5" },
    purple: { bg: "#6c63ff18", border: "#6c63ff40", text: "#6c63ff" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600",
    }}>
      {text}
    </span>
  );
}

// ── Section Header ─────────────────────────────────────────────
function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: "10px", marginTop: "4px" }}>
      <div style={{ fontSize: "13px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>
        {title}
      </div>
      {sub && <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

// ── Mini Stat Card (for summary grids) ──────────────────────────
function MiniCard({ label, value, sub, color, full }) {
  const colorMap = {
    amber:  "#ffa726",
    red:    "#ff5252",
    green:  "#4caf50",
    blue:   "#42a5f5",
    purple: "#6c63ff",
    gray:   "#8888a8",
  };
  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
      padding: "10px 6px", textAlign: "center", minWidth: 0,
      gridColumn: full ? "span 3" : undefined,
    }}>
      <div style={{ fontSize: "9px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
        {label}
      </div>
      <div style={{ fontSize: "15px", fontWeight: "700", color: colorMap[color] || "#e8e8f0" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: "9px", color: "#555570", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

// ── Maintenance Summary Grid ──────────────────────────────────────
function MaintenanceSummaryGrid({ data, paidFlats = [], pendingFlats = [] }) {
  const colorMap = {
    amber: "#ffa726", red: "#ff5252", green: "#4caf50",
    blue: "#42a5f5", purple: "#6c63ff", gray: "#8888a8",
  };

  function StatCard({ label, value, color, flats, full }) {
    return (
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
        padding: "10px 8px", textAlign: "center", minWidth: 0,
        gridColumn: full ? "span 3" : undefined,
      }}>
        <div style={{ fontSize: "9px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
          {label}
        </div>
        <div style={{ fontSize: "15px", fontWeight: "700", color: colorMap[color] || "#e8e8f0" }}>
          {value}
        </div>
        {flats && flats.length > 0 && (
          <div style={{ marginTop: "6px", fontSize: "9px", color: "#555570", lineHeight: 1.6 }}>
            {flats.map(f => f.flatId).join(", ")}
          </div>
        )}
        {flats && flats.length === 0 && (
          <div style={{ fontSize: "9px", color: "#555570", marginTop: "4px" }}>none</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "16px" }}>
      <StatCard label="Opening Balance"  value={fmt(data.openingBalance)} color="gray" />
      <StatCard label="Income"            value={fmt(data.collected)}      color="green"  flats={paidFlats} />
      <StatCard label="Pending"           value={fmt(data.outstanding)}    color="red"    flats={pendingFlats} />
      <StatCard label="Expenses"          value={fmt(data.expenses)}       color="amber" />
      <StatCard label="Balance"           value={fmt(data.balanceInHand)}  color={data.balanceInHand < 0 ? "red" : "blue"} />
      {data.outstanding > 0 && (
        <StatCard label="If All Pay Today" value={fmt(data.tentative)} color="purple" />
      )}
    </div>
  );
}

// ── Water / Tanker Summary Grid ──────────────────────────────────
// Row 1: Water Deposit (Revolving corpus) | Tanker Expenses | Tankers Ordered
// Row 2: Income (with paid flat nos)      | Pending (with pending flat nos) | Deposit Remaining
// All values are derived from settings / tankerEntries / waterBills — no hardcoding.
function WaterSummaryGrid({ data, paidFlats = [], pendingFlats = [] }) {
  const colorMap = {
    amber: "#ffa726", red: "#ff5252", green: "#4caf50",
    blue: "#42a5f5", purple: "#6c63ff", gray: "#8888a8",
  };
  const monthSub = data.billedMonth ? formatMonth(data.billedMonth) : "this month";

  function StatCard({ label, value, sub, color, flats }) {
    return (
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
        padding: "10px 6px", textAlign: "center", minWidth: 0,
      }}>
        <div style={{ fontSize: "9px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
          {label}
        </div>
        <div style={{ fontSize: "15px", fontWeight: "700", color: colorMap[color] || "#e8e8f0" }}>
          {value}
        </div>
        {sub && !flats && (
          <div style={{ fontSize: "9px", color: "#555570", marginTop: "2px" }}>{sub}</div>
        )}
        {flats && flats.length > 0 && (
          <div style={{ marginTop: "6px", fontSize: "9px", color: "#555570", lineHeight: 1.6 }}>
            {flats.map(f => f.flatId).join(", ")}
          </div>
        )}
        {flats && flats.length === 0 && (
          <div style={{ fontSize: "9px", color: "#555570", marginTop: "4px" }}>none</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "16px" }}>
      {/* Row 1 */}
      <StatCard label="Water Deposit (Revolving)" value={fmt(data.deposit)}     sub="corpus"  color="gray" />
      <StatCard label="Tanker Expenses"           value={fmt(data.tankerCost)}  sub={monthSub} color="amber" />
      <StatCard label="Tankers Ordered"           value={data.tankerCount}      sub={monthSub} color="gray" />

      {/* Row 2 */}
      <StatCard label="Income"           value={fmt(data.collected)}   color="green" flats={paidFlats} />
      <StatCard label="Pending"          value={fmt(data.outstanding)} color={data.outstanding === 0 ? "green" : "red"} flats={pendingFlats} />
      <StatCard label="Deposit Remaining" value={fmt(data.balance)}    sub="after payments" color="blue" />
    </div>
  );
}

// ── Ledger Table (Maintenance / Water) ──────────────────────────
// row is derived from real payments / waterBills data.
function LedgerTable({ title, icon, row, type }) {
  const cols = [
    { key: "carryForward",    label: "Brought Fwd" },
    { key: "presentMonthDue", label: "This Month" },
    { key: "totalOutstanding",label: "Total" },
    { key: "paid",            label: "Paid" },
    { key: "paidDate",        label: "Paid Date" },
    { key: "balance",         label: "Balance" },
  ];

  // Both maintenance and water: positive balance = owed (red).
  const isOwed = (row.balance || 0) > 0;
  const balanceColor = isOwed ? "#ff5252" : "#4caf50";

  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0", marginBottom: "8px" }}>
        {icon} {title}
      </div>
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
        overflowX: "auto",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", minWidth: "480px" }}>
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.key} style={{
                  textAlign: "right", padding: "8px 10px", color: "#8888a8",
                  fontWeight: "600", fontSize: "10px", textTransform: "uppercase",
                  letterSpacing: "0.3px", borderBottom: "1px solid #2e2e40",
                  whiteSpace: "nowrap",
                }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", whiteSpace: "nowrap" }}>
                {fmt(row.carryForward)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", whiteSpace: "nowrap" }}>
                {fmt(row.presentMonthDue)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", fontWeight: "600", whiteSpace: "nowrap" }}>
                {fmt(row.totalOutstanding)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#4caf50", whiteSpace: "nowrap" }}>
                {row.paid != null ? fmt(row.paid) : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#8888a8", whiteSpace: "nowrap" }}>
                {row.paidDate || "—"}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: balanceColor, fontWeight: "700", whiteSpace: "nowrap" }}>
                {fmt(row.balance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Non-Payee Summary (flat numbers + total amount only) ────────
// flatNos / totals come from real payments / waterBills data.
function NonPayeeSummary({ maintenanceNonPayees, waterNonPayees }) {
  const maintTotal  = maintenanceNonPayees.reduce((s, f) => s + (f.amount || 0), 0);
  const waterTotal  = waterNonPayees.reduce((s, f) => s + (f.amount || 0), 0);
  const grandTotal  = maintTotal + waterTotal;

  const Row = ({ label, items, total, color }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "10px 0", borderBottom: "1px solid #1e1e2e", gap: "12px",
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "4px" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "#e8e8f0", lineHeight: 1.6, wordBreak: "break-word" }}>
          {items.length === 0
            ? <span style={{ color: "#555570" }}>None</span>
            : items.map(f => `Flat ${f.flatId}`).join(", ")}
        </div>
      </div>
      <div style={{ fontSize: "14px", fontWeight: "700", color, whiteSpace: "nowrap" }}>
        {fmt(total)}
      </div>
    </div>
  );

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
      padding: "14px 16px", marginBottom: "16px",
    }}>
      <Row label="Maintenance — non-paying flats" items={maintenanceNonPayees} total={maintTotal} color="#ffa726" />
      <Row label="Water — non-paying flats" items={waterNonPayees} total={waterTotal} color="#42a5f5" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "10px" }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: "#8888a8" }}>Total</div>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "#ff5252" }}>{fmt(grandTotal)}</div>
      </div>
    </div>
  );
}

// ── Non-Payee Table — combined (MC dashboard only) ──────────────
// flatId / amounts come from real payments / waterBills data.
// Columns: Flat No | Maintenance due | Water due | Total
// Bottom row: Maintenance total | Water total | Grand total
function NonPayeeTable({ maintenanceNonPayees, waterNonPayees }) {
  const order = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];

  // Merge maintenance + water non-payees into one row per flat
  const byFlat = {};
  maintenanceNonPayees.forEach(f => {
    byFlat[f.flatId] = byFlat[f.flatId] || { flatId: f.flatId, maintenance: 0, water: 0 };
    byFlat[f.flatId].maintenance += f.amount || 0;
  });
  waterNonPayees.forEach(f => {
    byFlat[f.flatId] = byFlat[f.flatId] || { flatId: f.flatId, maintenance: 0, water: 0 };
    byFlat[f.flatId].water += f.amount || 0;
  });

  const rows = Object.values(byFlat).sort(
    (a, b) => order.indexOf(a.flatId) - order.indexOf(b.flatId)
  );

  const maintTotal = rows.reduce((s, r) => s + r.maintenance, 0);
  const waterTotal = rows.reduce((s, r) => s + r.water, 0);
  const grandTotal = maintTotal + waterTotal;

  const th = (align) => ({
    textAlign: align, padding: "8px 10px", color: "#8888a8",
    fontWeight: "600", fontSize: "10px", textTransform: "uppercase",
    letterSpacing: "0.3px", borderBottom: "1px solid #2e2e40",
    whiteSpace: "nowrap",
  });
  const td = (align, color, weight) => ({
    textAlign: align, padding: "10px", color: color || "#e8e8f0",
    fontWeight: weight || "400", whiteSpace: "nowrap",
  });
  const tdTotal = (align, color, weight) => ({
    ...td(align, color, weight), borderTop: "1px solid #2e2e40",
  });

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
      overflowX: "auto", marginBottom: "16px",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", minWidth: "420px" }}>
        <thead>
          <tr>
            <th style={th("left")}>Flat No</th>
            <th style={th("right")}>Maintenance Due</th>
            <th style={th("right")}>Water Due</th>
            <th style={th("right")}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: "center", padding: "16px", color: "#555570" }}>
                All flats paid up 🎉
              </td>
            </tr>
          ) : rows.map(r => (
            <tr key={r.flatId}>
              <td style={td("left", "#e8e8f0", "600")}>Flat {r.flatId}</td>
              <td style={td("right", r.maintenance > 0 ? "#ffa726" : "#555570")}>
                {r.maintenance > 0 ? fmt(r.maintenance) : "—"}
              </td>
              <td style={td("right", r.water > 0 ? "#42a5f5" : "#555570")}>
                {r.water > 0 ? fmt(r.water) : "—"}
              </td>
              <td style={td("right", "#ff5252", "700")}>
                {fmt(r.maintenance + r.water)}
              </td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr>
              <td style={tdTotal("left", "#8888a8", "600")}>Total</td>
              <td style={tdTotal("right", "#ffa726", "700")}>{fmt(maintTotal)}</td>
              <td style={tdTotal("right", "#42a5f5", "700")}>{fmt(waterTotal)}</td>
              <td style={tdTotal("right", "#ff5252", "700")}>{fmt(grandTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ── Bank Screen moved to its own file: BankScreen.jsx ──────────
// (used from inside PassbookScreen's "My Passbook" view now, not as
// its own top-level tab)

// ── Tab Bar ────────────────────────────────────────────────────
export const TABS = [
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { id: "flats",     label: "Flats",     icon: "🏠" },
  { id: "finance",   label: "Passbook",   icon: "💰" },
  { id: "water",     label: "Water",     icon: "💧" },
  { id: "poll",      label: "Poll",      icon: "🗳️" },
  { id: "tasks",     label: "Tasks",     icon: "✅" },
  { id: "chat",      label: "Chat",      icon: "💬" },
  { id: "contacts",  label: "Contacts",  icon: "📞" },
  { id: "bylaws",    label: "Bylaws",    icon: "📜" },
  { id: "reports",   label: "Reports",   icon: "📄" },
  { id: "settings",  label: "Settings",  icon: "⚙️", mcOnly: true },
  // { id: "tankerreset", label: "Tanker Reset", icon: "🚛", presidentOnly: true },
];

// ── Coming Soon placeholder ────────────────────────────────────
function ComingSoon({ tab }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: "40px", marginBottom: "12px" }}>
        {TABS.find(t => t.id === tab)?.icon || "🔧"}
      </div>
      <div style={{ fontSize: "15px", fontWeight: "600", color: "#e8e8f0", marginBottom: "6px" }}>
        {TABS.find(t => t.id === tab)?.label} — Coming Soon
      </div>
      <div style={{ fontSize: "12px", color: "#555570" }}>This screen is under development.</div>
    </div>
  );
}

// ── UPI Payment Modal ──────────────────────────────────────────
export function buildUpiLink({ upiId, societyName, amount, note }) {
  return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(societyName)}&am=${amount}&tn=${encodeURIComponent(note)}&cu=INR`;
}

export function buildUpiNote({ flatId, items }) {
  const now = new Date();
  const mon = now.toLocaleString("en-IN", { month: "short" });
  const yr  = String(now.getFullYear()).slice(2);
  return `F${flatId}-${items.join("+")}-${mon}${yr}`.slice(0, 50);
}

export function extractShortCode(title) {
  const stop = new Set(["by","the","of","for","and","in","a","an","to","from","with","on","at","is","was"]);
  const words = (title || "").split(/\s+/).filter(w => !stop.has(w.toLowerCase()));
  return words.slice(0, 2).join("").replace(/[^a-zA-Z0-9]/g, "").slice(0, 10) || "SocWork";
}

export function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function UpiPayModal({ flatId, settings, maintAmt, maintDue, waterAmt, waterDue, waterMonth, adhocItems, onClose }) {
  const upiId       = settings?.upi1 || "";
  const societyName = settings?.societyName || "Your Society";
  const isMobile    = isMobileDevice();

  const hasMaint = maintDue && maintAmt > 0;
  const hasWater = waterDue && waterAmt > 0;

  const [payMaint,  setPayMaint]  = useState(hasMaint);
  const [payWater,  setPayWater]  = useState(hasWater);
  const [expanded,  setExpanded]  = useState(false);
  const [adhocTarget, setAdhocTarget] = useState(null);
  const [upiOpened, setUpiOpened] = useState(false);
  const [upiLinkFailed, setUpiLinkFailed] = useState(false);
  const [copied,    setCopied]    = useState(null);

  const totalAmt = adhocTarget
    ? adhocTarget.amount
    : (payMaint ? maintAmt : 0) + (payWater ? waterAmt : 0);

  const upiNote = adhocTarget
    ? buildUpiNote({ flatId, items: [extractShortCode(adhocTarget.eventTitle)] })
    : buildUpiNote({ flatId, items: [...(payMaint ? ["Maint"] : []), ...(payWater ? ["Water"] : [])] });

  const upiLink = buildUpiLink({ upiId, societyName, amount: totalAmt, note: upiNote });
  const noSelection = !adhocTarget && !payMaint && !payWater;

  function handlePay() {
    const start = Date.now();
    window.location.href = upiLink;
    setTimeout(() => { if (Date.now() - start < 2500) setUpiLinkFailed(true); }, 2000);
    setUpiOpened(true);
  }

  async function handleCopy(text, key) {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); }
    catch { /* ignore */ }
  }

  const overlay = { position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" };
  const sheet   = { background: "#1a1a24", borderRadius: "20px 20px 0 0", padding: "20px", width: "100%", maxWidth: "640px", maxHeight: "90vh", overflowY: "auto" };

  return (
    <div style={overlay}>
      <div style={sheet}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
            {adhocTarget ? `Pay — ${adhocTarget.eventTitle}` : "Pay Dues"}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "20px", cursor: "pointer" }}>×</button>
        </div>

        {/* Adhoc back button */}
        {adhocTarget && (
          <button onClick={() => { setAdhocTarget(null); setUpiOpened(false); setUpiLinkFailed(false); }}
            style={{ background: "none", border: "none", color: "#6c63ff", fontSize: "12px", cursor: "pointer", marginBottom: "12px", padding: 0 }}>
            ← Back to all dues
          </button>
        )}

        {/* Society Work items */}
        {!adhocTarget && adhocItems.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px" }}>Society Work Dues</div>
            {adhocItems.map((item, i) => (
              <div key={i} style={{ background: "#12121a", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 14px", marginBottom: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{item.eventTitle}</div>
                  <div style={{ fontSize: "12px", color: "#8888a8" }}>{"₹" + Number(item.amount || 0).toLocaleString("en-IN")}</div>
                </div>
                <button onClick={() => { setAdhocTarget(item); setUpiOpened(false); setUpiLinkFailed(false); }}
                  style={{ background: "#6c63ff", color: "#fff", border: "none", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                  PAY
                </button>
              </div>
            ))}
            {(hasMaint || hasWater) && <div style={{ borderTop: "1px solid #2e2e40", marginTop: "10px", paddingTop: "10px" }} />}
          </div>
        )}

        {/* Maintenance + Water selection */}
        {!adhocTarget && (hasMaint || hasWater) && (
          <div style={{ marginBottom: "14px" }}>
            {!expanded ? (
              <div style={{ background: "#12121a", border: "1px solid #2e2e40", borderRadius: "12px", padding: "14px 16px", marginBottom: "8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "4px" }}>
                      {payMaint && payWater ? "Maintenance + Water" : payMaint ? "Maintenance" : "Water"}
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: "700", color: "#e8e8f0" }}>{"₹" + Number(totalAmt).toLocaleString("en-IN")}</div>
                  </div>
                  <button onClick={() => setExpanded(true)} style={{ background: "none", border: "1px solid #2e2e40", borderRadius: "8px", padding: "6px 12px", color: "#8888a8", fontSize: "11px", cursor: "pointer" }}>
                    Change ↓
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: "#12121a", border: "1px solid #6c63ff40", borderRadius: "12px", padding: "14px 16px", marginBottom: "8px" }}>
                <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>Select what to pay</div>
                {hasMaint && (
                  <div onClick={() => setPayMaint(p => !p)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: hasWater ? "1px solid #2e2e40" : "none", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ width: "20px", height: "20px", borderRadius: "5px", border: `2px solid ${payMaint ? "#6c63ff" : "#2e2e40"}`, background: payMaint ? "#6c63ff" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {payMaint && <span style={{ color: "#fff", fontSize: "12px" }}>✓</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>🏠 Maintenance</div>
                        <div style={{ fontSize: "11px", color: "#8888a8" }}>{formatMonth(currentMonthKey())}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{"₹" + Number(maintAmt).toLocaleString("en-IN")}</div>
                  </div>
                )}
                {hasWater && (
                  <div onClick={() => setPayWater(p => !p)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ width: "20px", height: "20px", borderRadius: "5px", border: `2px solid ${payWater ? "#6c63ff" : "#2e2e40"}`, background: payWater ? "#6c63ff" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {payWater && <span style={{ color: "#fff", fontSize: "12px" }}>✓</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>💧 Water</div>
                        <div style={{ fontSize: "11px", color: "#8888a8" }}>{formatMonth(waterMonth)}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{"₹" + Number(waterAmt).toLocaleString("en-IN")}</div>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #2e2e40" }}>
                  <span style={{ fontSize: "12px", color: "#8888a8", fontWeight: "600" }}>Total</span>
                  <span style={{ fontSize: "18px", fontWeight: "700", color: "#6c63ff" }}>{"₹" + Number(totalAmt).toLocaleString("en-IN")}</span>
                </div>
                <button onClick={() => setExpanded(false)} style={{ width: "100%", marginTop: "10px", background: "none", border: "1px solid #2e2e40", borderRadius: "8px", padding: "7px", color: "#8888a8", fontSize: "12px", cursor: "pointer" }}>Done ↑</button>
              </div>
            )}
          </div>
        )}

        {/* Transaction note preview */}
        {!noSelection && totalAmt > 0 && (
          <div style={{ background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", color: "#555570", marginBottom: "4px" }}>Will appear in your UPI history as</div>
            <div style={{ fontSize: "12px", color: "#42a5f5", fontFamily: "monospace" }}>{upiNote}</div>
          </div>
        )}

        {/* Desktop: QR code */}
        {!isMobile && !noSelection && totalAmt > 0 && (
          <div style={{ textAlign: "center", padding: "10px 0 6px" }}>
            <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "14px" }}>Scan this QR with any UPI app on your phone</div>
            <div style={{ display: "inline-block", background: "#fff", padding: "16px", borderRadius: "16px" }}>
              <QRCodeSVG value={upiLink} size={200} />
            </div>
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "10px" }}>{"₹" + Number(totalAmt).toLocaleString("en-IN")} · {upiId}</div>
          </div>
        )}

        {/* Mobile: PAY button or fallback */}
        {isMobile && !noSelection && totalAmt > 0 && (
          <>
            {!upiOpened && (
              <button onClick={handlePay} style={{ width: "100%", background: "linear-gradient(135deg, #6c63ff, #4caf50)", color: "#fff", border: "none", borderRadius: "14px", padding: "15px", fontSize: "16px", fontWeight: "700", cursor: "pointer", marginTop: "4px" }}>
                PAY {"₹" + Number(totalAmt).toLocaleString("en-IN")}
              </button>
            )}
            {upiOpened && !upiLinkFailed && (
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <div style={{ fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>✅ UPI app should have opened. Complete payment there.</div>
                <button onClick={() => { setUpiOpened(false); setUpiLinkFailed(false); }} style={{ background: "none", border: "1px solid #2e2e40", color: "#8888a8", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", cursor: "pointer" }}>Try again</button>
              </div>
            )}
            {upiLinkFailed && (
              <div style={{ background: "#1a1a24", border: "1px solid #ffa72640", borderRadius: "12px", padding: "14px" }}>
                <div style={{ fontSize: "12px", color: "#ffa726", marginBottom: "12px" }}>⚠ Could not open UPI app automatically. Copy details below and pay manually.</div>
                {[{ label: "UPI ID", value: upiId, key: "upi" }, { label: "Amount", value: String(totalAmt), key: "amount" }, { label: "Note", value: upiNote, key: "note" }].map(row => (
                  <div key={row.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #2e2e40" }}>
                    <div>
                      <div style={{ fontSize: "10px", color: "#555570" }}>{row.label}</div>
                      <div style={{ fontSize: "13px", color: "#e8e8f0", fontFamily: "monospace" }}>{row.value}</div>
                    </div>
                    <button onClick={() => handleCopy(row.value, row.key)} style={{ background: copied === row.key ? "#4caf5020" : "#2e2e40", border: "none", borderRadius: "6px", padding: "5px 10px", color: copied === row.key ? "#4caf50" : "#8888a8", fontSize: "11px", cursor: "pointer" }}>
                      {copied === row.key ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {noSelection && <div style={{ textAlign: "center", padding: "20px", color: "#555570", fontSize: "13px" }}>Select at least one item to pay.</div>}
        <div style={{ fontSize: "11px", color: "#555570", textAlign: "center", marginTop: "14px" }}>Payment confirmation is automatic via bank alert — no screenshot needed.</div>
      </div>
    </div>
  );
}

// ── My Dues — own flat's ledger + status banner ─────────────────
// Used on ResidentDashboard, and on MCDashboard so MC members (who
// are also flat owners) can see their own dues alongside society data.
// Ledger values come from the flat's payments doc (maintenance) and waterBills doc (water).
// Responsibility for paying is determined by activeTenant's paysMaintenance/paysWater fields.
function MyDuesSection({ user, flats, payments, maintenanceBills, waterBills, settings, month, activeTenant, onRefresh }) {
  const today  = new Date().getDate();
  const cutoff = settings?.dueCutoffDay || 10;

  // Modal state for pay
  const [showPayModal, setShowPayModal] = useState(false);

  // Commented out 2026-07-11: only the popup-open flag is unused now; `declaring`
  // and `declareError` are kept because the still-active PAY button banner reads them.
  // const [showDeclareModal, setShowDeclareModal] = useState(false);
  const [declaring,        setDeclaring]        = useState(false);
  const [declareError,     setDeclareError]     = useState("");

  // ── Society Work (Adhoc) pending payments ─────────────────────
  const [adhocDues,         setAdhocDues]         = useState([]);
  const [adhocDeclaring,    setAdhocDeclaring]    = useState(null); // docId being declared
  const [adhocDeclareError, setAdhocDeclareError] = useState("");

  useEffect(() => {
    if (!user?.flatId) return;
    async function loadAdhoc() {
      try {
        const snap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("flatId", "==", user.flatId)
        ));
        const docs = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
        // Show only unpaid / not yet confirmed — treat missing status as "pending"
        const pending = docs.filter(d =>
          d.paid !== true && d.status !== "confirmed"
        );
        setAdhocDues(pending);
      } catch (e) {
        console.error("Failed to load adhoc dues:", e);
      }
    }
    loadAdhoc();
  }, [user?.flatId]);

  async function handleAdhocDeclare(adhoc) {
    setAdhocDeclaring(adhoc.docId);
    setAdhocDeclareError("");
    try {
      await updateDoc(doc(db, "adhocPayments", adhoc.docId), {
        status:      "declared",
        declaredBy:  user.name || "Resident",
        declaredAt:  serverTimestamp(),
      });
      // Refresh adhoc list locally
      setAdhocDues(prev => prev.map(d =>
        d.docId === adhoc.docId ? { ...d, status: "declared" } : d
      ));
    } catch (e) {
      setAdhocDeclareError("Failed to declare. Please try again.");
    } finally {
      setAdhocDeclaring(null);
    }
  }

  // ── Who is responsible for what? ──────────────────────────────
  // If no active tenant → Owner pays everything.
  // If active tenant → check paysMaintenance / paysWater on the tenant doc.
  //   paysMaintenance: true  means TENANT pays maintenance (owner does NOT)
  //   paysWater: true        means TENANT pays water (owner does NOT)
  const hasTenant          = !!activeTenant;
  const tenantPaysMaint    = hasTenant && activeTenant.paysMaintenance === true;
  const tenantPaysWater    = hasTenant && activeTenant.paysWater       === true;
  const ownerPaysMaint     = !tenantPaysMaint;  // owner pays maintenance if tenant doesn't
  const ownerPaysWater     = !tenantPaysWater;  // owner pays water if tenant doesn't
  const ownerPaysBoth      = ownerPaysMaint && ownerPaysWater;

  // ── Maintenance payment status ─────────────────────────────────
  const myFlat         = flats.find(f => f.flatId === user.flatId);

  const maintPayment = payments.find(
    p => p.flatId === user.flatId && p.month === month &&
         p.status !== "rejected" &&
         (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
  );
  const maintPaid     = maintPayment?.status === "confirmed";
  // Commented out 2026-07-11: the "declared / Awaiting MC" flow is superseded by
  // bank-sync auto-confirmation. Left here (not deleted) in case we bring back a
  // manual-declare step later. Forced to false so a stale "declared" record can
  // never hide the PAY button again.
  // const maintDeclared = maintPayment?.status === "declared";
  const maintDeclared = false;
  const maintOverdue  = !maintPaid && !maintDeclared && today > cutoff;

  const carryForwardIn  = maintPayment?.carryForwardIn ?? 0;

  // Brought Forward formula (session summary, Section 3.2 & 3.5) — this
  // replaces the old carryForwardIn field, which was never written by
  // anything and always showed ₹0.
  const ledger = computeMaintenanceLedger({ flat: myFlat, payments, maintenanceBills, settings, month });
  const maintenanceLedger = {
    carryForward:       ledger.broughtForward,
    presentMonthDue:    ledger.presentMonthDue,
    totalOutstanding:   ledger.totalOutstanding,
    paid:               maintPaid ? ledger.presentMonthDue : null,
    paidDate:           maintPaid && maintPayment?.confirmedAt
      ? new Date(maintPayment.confirmedAt?.seconds * 1000).toLocaleDateString("en-IN")
      : null,
    balance:            ledger.totalOutstanding,
  };

  // ── Water bill status ──────────────────────────────────────────
  // Water bills are calculated for the PREVIOUS month (e.g. May when current month is June).
  // So we look at the last billed month, not the current month.
  const fyMonths    = fyMonthKeys();
  const waterMonth  = fyMonths.length >= 2 ? fyMonths[fyMonths.length - 2] : fyMonths[0];
  const myWaterBill = waterBills.find(w => w.flatId === user.flatId && w.month === waterMonth);
  const waterAmt    = myWaterBill
    ? (myWaterBill.carryForward || 0) + (myWaterBill.billAmount || 0)
    : 0;

  const waterPayment = payments.find(
    p => p.flatId === user.flatId && p.month === month &&
         p.status !== "rejected" &&
         (!p.type || p.type.toLowerCase() === "water" || p.type.toLowerCase() === "both")
  );
  const waterPaid     = myWaterBill?.paid || waterPayment?.status === "confirmed";
  // Commented out 2026-07-11: same reason as maintDeclared above — see that note.
  // const waterDeclared = !waterPaid && waterPayment?.status === "declared";
  const waterDeclared = false;
  const waterOverdue  = !waterPaid && !waterDeclared && today > cutoff;

  const waterLedger = myWaterBill
    ? {
        carryForward:     myWaterBill.carryForward || 0,
        presentMonthDue:  myWaterBill.billAmount   || 0,
        totalOutstanding: (myWaterBill.carryForward || 0) + (myWaterBill.billAmount || 0),
        paid:             waterPaid ? (myWaterBill.billAmount || 0) : null,
        paidDate:         waterPaid && myWaterBill.paidDate
          ? new Date(myWaterBill.paidDate).toLocaleDateString("en-IN")
          : null,
        balance:          Math.abs(myWaterBill.balance ?? (myWaterBill.totalDue || 0)),
      }
    : { carryForward: 0, presentMonthDue: 0, totalOutstanding: 0, paid: null, paidDate: null, balance: 0 };

  // Commented out 2026-07-11: these built the old "declare and wait for MC" flow.
  // Nothing in the current PAY button calls these anymore — kept here in case
  // a manual-declare step is needed again in future.
  /*
  async function writeDeclare(type, amount) {
    await addDoc(collection(db, "payments"), {
      flatId:        user.flatId,
      month,
      type,
      amount,
      status:        "declared",
      declaredBy:    user.name || "Resident",
      declaredAt:    serverTimestamp(),
      autoConfirmed: false,
    });
  }

  async function handleDeclareChoice(choice) {
    setDeclaring(true);
    setDeclareError("");
    try {
      if (choice === "maintenance") {
        await writeDeclare("maintenance", maintenanceLedger.totalOutstanding);
      } else if (choice === "water") {
        await writeDeclare("water", waterAmt);
      } else {
        // "both" — write one combined payment doc
        await writeDeclare("both", maintenanceLedger.totalOutstanding + waterAmt);
      }
      setShowDeclareModal(false);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setDeclareError("Failed to declare. Please try again.");
    } finally {
      setDeclaring(false);
    }
  }

  // ── Single-bill declare (when only one bill is the owner's responsibility) ──
  async function handleSingleDeclare(type) {
    setDeclaring(true);
    setDeclareError("");
    try {
      const amount = type === "maintenance" ? maintenanceLedger.totalOutstanding : waterAmt;
      await writeDeclare(type, amount);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setDeclareError("Failed to declare. Please try again.");
    } finally {
      setDeclaring(false);
    }
  }
  */

  // ── Status banner helper ───────────────────────────────────────
  function BillBanner({ label, isPaid, isDeclared, isOverdue, canDeclare, onDeclare, onOpenModal }) {
    let color = "#ffa726", text = "Pending", badge = "amber";
    if (isPaid)          { color = "#4caf50"; text = "Paid ✅";               badge = "green"; }
    else if (isDeclared) { color = "#ffa726"; text = "Declared — Awaiting MC"; badge = "amber"; }
    else if (isOverdue)  { color = "#ff5252"; text = "Overdue";               badge = "red";   }

    return (
      <div style={{
        background: "#1a1a24", border: `1px solid ${color}40`,
        borderLeft: `3px solid ${color}`,
        borderRadius: "14px", padding: "14px 16px", marginBottom: "12px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <span style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px", display: "block" }}>{label}</span>
          <Badge text={text} color={badge} />
        </div>
        {canDeclare && !isPaid && !isDeclared && (
          <button
            disabled={declaring}
            style={{
              background: declaring ? "#2e2e40" : "linear-gradient(135deg, #6c63ff, #4caf50)",
              color: "#fff", border: "none", borderRadius: "10px", padding: "10px 16px",
              fontSize: "13px", fontWeight: "700",
              cursor: declaring ? "not-allowed" : "pointer", opacity: declaring ? 0.7 : 1,
            }}
            onClick={() => setShowPayModal(true)}
          >
            PAY
          </button>
        )}
        {!canDeclare && !isPaid && (
          <span style={{ fontSize: "11px", color: "#8888a8", fontStyle: "italic" }}>
            {hasTenant ? "Tenant responsible" : ""}
          </span>
        )}
        {isDeclared && <span style={{ fontSize: "12px", color: "#ffa726" }}>⏳ Awaiting MC</span>}
        {isPaid     && <span style={{ fontSize: "12px", color: "#4caf50" }}>✅ Confirmed by MC</span>}
      </div>
    );
  }

  // Commented out 2026-07-11: the "Declare Payment" popup — the PAY button no
  // longer opens this (it goes straight to UpiPayModal instead). Kept in case
  // a manual-declare step is needed again in future.
  /*
  const bothAmt = maintenanceLedger.totalOutstanding + waterAmt;
  const DeclareModal = () => (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
    }}>
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "18px",
        padding: "24px", width: "300px", maxWidth: "90vw",
      }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e0e0f0", marginBottom: "6px" }}>
          Declare Payment
        </div>
        <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "20px" }}>
          What are you paying this month?
        </div>

        {!maintPaid && !maintDeclared && (
          <button
            disabled={declaring}
            onClick={() => handleDeclareChoice("maintenance")}
            style={{
              width: "100%", background: "#2e2e40", color: "#e0e0f0",
              border: "1px solid #3e3e58", borderRadius: "12px",
              padding: "14px 16px", fontSize: "14px", fontWeight: "600",
              cursor: declaring ? "not-allowed" : "pointer", marginBottom: "10px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <span>🏠 Maintenance</span>
            <span style={{ color: "#6c63ff" }}>₹{maintenanceLedger.totalOutstanding.toLocaleString("en-IN")}</span>
          </button>
        )}

        {myWaterBill && !waterPaid && !waterDeclared && (
          <button
            disabled={declaring}
            onClick={() => handleDeclareChoice("water")}
            style={{
              width: "100%", background: "#2e2e40", color: "#e0e0f0",
              border: "1px solid #3e3e58", borderRadius: "12px",
              padding: "14px 16px", fontSize: "14px", fontWeight: "600",
              cursor: declaring ? "not-allowed" : "pointer", marginBottom: "10px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <span>💧 Water</span>
            <span style={{ color: "#6c63ff" }}>₹{waterAmt.toLocaleString("en-IN")}</span>
          </button>
        )}

        {!maintPaid && !maintDeclared && myWaterBill && !waterPaid && !waterDeclared && (
          <button
            disabled={declaring}
            onClick={() => handleDeclareChoice("both")}
            style={{
              width: "100%", background: "#6c63ff", color: "#fff",
              border: "none", borderRadius: "12px",
              padding: "14px 16px", fontSize: "14px", fontWeight: "700",
              cursor: declaring ? "not-allowed" : "pointer", marginBottom: "10px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <span>🏠💧 Both</span>
            <span>₹{bothAmt.toLocaleString("en-IN")}</span>
          </button>
        )}

        {declareError && (
          <div style={{ fontSize: "11px", color: "#ff5252", marginBottom: "10px" }}>{declareError}</div>
        )}

        <button
          onClick={() => { setShowDeclareModal(false); setDeclareError(""); }}
          style={{
            width: "100%", background: "none", color: "#8888a8",
            border: "1px solid #2e2e40", borderRadius: "10px",
            padding: "10px", fontSize: "13px", cursor: "pointer", marginTop: "4px",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
  */


  return (
    <>
      <SectionHeader title="My Dues" sub={`Flat ${user.flatId} · ${formatMonth(month)}`} />

      {/* Always show both ledgers for visibility */}
      <LedgerTable title={`Maintenance — ${formatMonth(month)}`} icon="🏠" row={maintenanceLedger} />
      <LedgerTable title={`Water — ${formatMonth(waterMonth)}`} icon="💧" row={waterLedger} type="water" />

      {/* ── Banners ── */}
      {ownerPaysBoth ? (
        <BillBanner
          label="Maintenance & Water"
          isPaid={maintPaid && waterPaid}
          isDeclared={maintDeclared || waterDeclared}
          isOverdue={maintOverdue || waterOverdue}
          canDeclare={true}
        />
      ) : (
        <>
          {/* Maintenance banner */}
          <BillBanner
            label="Maintenance"
            isPaid={maintPaid}
            isDeclared={maintDeclared}
            isOverdue={maintOverdue}
            canDeclare={ownerPaysMaint}
          />
          {/* Water banner */}
          <BillBanner
            label="Water"
            isPaid={waterPaid}
            isDeclared={waterDeclared}
            isOverdue={waterOverdue}
            canDeclare={ownerPaysWater}
          />
        </>
      )}

      {/* Commented out 2026-07-11: was tied to the "declared" popup flow. */}
      {/*
      {declareError && !showDeclareModal && (
        <div style={{ fontSize: "11px", color: "#ff5252", marginTop: "-8px", marginBottom: "14px" }}>
          {declareError}
        </div>
      )}

      {showDeclareModal && <DeclareModal />}
      */}

      {/* UPI Pay Modal */}
      {showPayModal && (
        <UpiPayModal
          flatId={user.flatId}
          settings={settings}
          maintAmt={maintenanceLedger.totalOutstanding}
          maintDue={!maintPaid && !maintDeclared && ownerPaysMaint}
          waterAmt={waterAmt}
          waterDue={!waterPaid && !waterDeclared && ownerPaysWater}
          waterMonth={waterMonth}
          adhocItems={adhocDues.filter(a => a.status !== "declared")}
          onClose={() => setShowPayModal(false)}
        />
      )}

      {/* ── Society Work (Adhoc) Pending Dues ── */}
      {adhocDues.length > 0 && (
        <>
          <div style={{
            fontSize: "13px", color: "#8888a8", textTransform: "uppercase",
            letterSpacing: "0.5px", fontWeight: "600", marginTop: "20px", marginBottom: "10px",
          }}>
            🏗️ Society Work
          </div>
          {adhocDues.map(adhoc => {
            const isDeclared = adhoc.status === "declared";
            const borderColor = isDeclared ? "#ffa726" : "#6c63ff";
            return (
              <div key={adhoc.docId} style={{
                background: "#1a1a24",
                border: `1px solid ${borderColor}40`,
                borderLeft: `3px solid ${borderColor}`,
                borderRadius: "14px", padding: "14px 16px", marginBottom: "12px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0", marginBottom: "4px" }}>
                    {adhoc.eventTitle || "Society Work"}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#6c63ff" }}>
                    ₹{Number(adhoc.amount || 0).toLocaleString("en-IN")}
                  </div>
                  {isDeclared && (
                    <span style={{
                      display: "inline-block", marginTop: "6px",
                      background: "#ffa72618", border: "1px solid #ffa72640",
                      color: "#ffa726", borderRadius: "6px",
                      padding: "2px 8px", fontSize: "11px", fontWeight: "600",
                    }}>
                      Declared — Awaiting MC
                    </span>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  {!isDeclared && (
                    <button
                      onClick={() => setShowPayModal(true)}
                      style={{
                        background: "linear-gradient(135deg, #6c63ff, #4caf50)",
                        color: "#fff", border: "none", borderRadius: "10px",
                        padding: "10px 16px", fontSize: "13px", fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      PAY
                    </button>
                  )}
                  {isDeclared && (
                    <span style={{ fontSize: "12px", color: "#ffa726" }}>⏳ Awaiting MC</span>
                  )}
                </div>
              </div>
            );
          })}
          {adhocDeclareError && (
            <div style={{ fontSize: "11px", color: "#ff5252", marginBottom: "14px" }}>
              {adhocDeclareError}
            </div>
          )}
        </>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// MC DASHBOARD VIEW
// ══════════════════════════════════════════════════════════════
function MCDashboard({ settings, flats, payments, maintenanceBills, expenses, maintenanceIncome, waterBills, tankers, user, activeTenant, onRefresh }) {
  const month = currentMonthKey();
  const fyMonths = fyMonthKeys();

  // ── Society Feedback state ──
  const [societyFeedback, setSocietyFeedback] = useState([]);
  const [fbLoading,       setFbLoading]       = useState(true);
  const [fbShowAll,       setFbShowAll]       = useState(false);
  const [fbOpen,          setFbOpen]          = useState(false);

  useEffect(() => {
    async function loadFeedback() {
      try {
        const snap = await getDocs(
          query(
            collection(db, "feedback"),
            where("category", "==", "society"),
            orderBy("submittedAt", "desc")
          )
        );
        setSocietyFeedback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Failed to load society feedback:", e);
      } finally {
        setFbLoading(false);
      }
    }
    loadFeedback();
  }, []);

  const isMaintenancePayment = p =>
    p.type?.toLowerCase() === "maintenance" || p.type?.toLowerCase() === "both" || !p.type;

  // ── Helper: collected and expenses for a given month ──
  // Includes flat dues (payments) + misc income (maintenanceIncome) for the month
  const collectedForMonth = (m) => {
    const duesTotal = payments
      .filter(p => p.month === m && p.status === "confirmed" && isMaintenancePayment(p))
      .reduce((s, p) => s + (p.amount || 0), 0);
    const miscTotal = maintenanceIncome
      .filter(i => i.month === m)
      .reduce((s, i) => s + (i.amount || 0), 0);
    return duesTotal + miscTotal;
  };

  const expensesForMonth = (m) =>
    expenses
      .filter(e => e.month === m && e.category !== "Water Tanker")
      .reduce((s, e) => s + (e.amount || 0), 0);

  // ── Opening Balance — computed by rolling forward from Apr-26 ──
  // Formula each month: opening + collected − expenses = closing → next month's opening
  // Apr opening = maintOpeningBalance from Firestore settings (not hardcoded)
  const APR_OPENING = settings?.maintOpeningBalance || 0;
  const openingBalance = fyMonths.slice(0, -1).reduce((bal, m) => {
    return bal + collectedForMonth(m) - expensesForMonth(m);
  }, APR_OPENING);
  // openingBalance is now the opening for the CURRENT month (June in Jun-26)

  // ── This Month — Maintenance ──
  const confirmedThisMonth = payments.filter(
    p => p.month === month && p.status === "confirmed" && isMaintenancePayment(p)
  );
  const collected = confirmedThisMonth.reduce((s, p) => s + (p.amount || 0), 0);

  const paidFlatIds = new Set(confirmedThisMonth.map(p => p.flatId));
  const outstandingFlats = flats.filter(f => !paidFlatIds.has(f.flatId));
  const outstanding = outstandingFlats.reduce((s, f) => s + (f.maintenanceRate || 1500), 0);

  const expensesThisMonth = expensesForMonth(month);

  // Balance = Opening Balance + Collected this month − Expenses this month
  const balanceInHand = openingBalance + collected - expensesThisMonth;

  // Flat lists for cards
  const paidFlats    = confirmedThisMonth.map(p => ({ flatId: p.flatId, amount: p.amount || 0 }));
  const pendingFlats = outstandingFlats.map(f => ({ flatId: f.flatId, amount: f.maintenanceRate || 1500 }));

  // Pending confirmations (declared but not confirmed)
  const pendingConfirm = payments.filter(p => p.month === month && p.status === "declared");

  const maintenanceThisMonth = {
    openingBalance,
    collected,
    outstanding,
    expenses: expensesThisMonth,
    balanceInHand,
    tentative: balanceInHand + outstanding,
  };

  // ── FY-to-Date — Maintenance ──
  const fyCollected = fyMonths.reduce((s, m) => s + collectedForMonth(m), 0);
  const fyExpenses  = fyMonths.reduce((s, m) => s + expensesForMonth(m), 0);
  const fyOutstanding = fyMonths.reduce((total, m) => {
    const paidThisMonth = new Set(
      payments
        .filter(p => p.month === m && p.status === "confirmed" && isMaintenancePayment(p))
        .map(p => p.flatId)
    );
    const unpaidThisMonth = flats
      .filter(f => !paidThisMonth.has(f.flatId))
      .reduce((s, f) => s + (f.maintenanceRate || 1500), 0);
    return total + unpaidThisMonth;
  }, 0);
  const fyBalanceInHand = APR_OPENING + fyCollected - fyExpenses;

  const maintenanceFY = {
    openingBalance: APR_OPENING,
    collected: fyCollected,
    outstanding: fyOutstanding,
    expenses: fyExpenses,
    balanceInHand: fyBalanceInHand,
    tentative: fyBalanceInHand + fyOutstanding,
  };

  // ── Water fund — from settings ──
  const waterFund = settings?.waterDeposit || 0;

  // ── This Month — Water ──
  // Billing lag: May's bills are collected in June.
  // "This month" water = last billed month's bills (May).
  const lastBilledMonth = fyMonths.length >= 2 ? fyMonths[fyMonths.length - 2] : fyMonths[0];
  const waterBillsLastBilled = waterBills.filter(w => w.month === lastBilledMonth);
  const waterCollected  = waterBillsLastBilled.filter(w => w.paid).reduce((s, w) => s + (w.billAmount || 0), 0);
  const waterOutstanding = waterBillsLastBilled.filter(w => !w.paid).reduce((s, w) => s + (w.billAmount || 0), 0);

  // Tanker Expenses / Tankers Ordered cards show the BILLED month's (May) tanker
  // activity — i.e. the same month the income/pending figures are for.
  const billedMonthTankers = tankers.filter(t => t.month === lastBilledMonth);
  const billedMonthTankerCount = billedMonthTankers.reduce((s, t) => s + (t.count || 0), 0);
  const billedMonthTankerCost  = billedMonthTankers.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);

  // Current month (June) tankers — live, ongoing ordering before bills are raised.
  // Kept separate for the "Tanker Summary" chip (in-progress display, refined later).
  const thisMonthTankers = tankers.filter(t => t.month === month);
  const tankerCount = thisMonthTankers.reduce((s, t) => s + (t.count || 0), 0);
  const tankerCost  = thisMonthTankers.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);

  // Deposit Remaining = Water Deposit − billed-month tanker cost + billed-month income collected
  // e.g. 36,000 − 12,100 (May tankers) + income-so-far = 23,900 + income-so-far
  const waterFundBalance = waterFund - billedMonthTankerCost + waterCollected;

  const waterThisMonth = {
    tankerCount: billedMonthTankerCount,
    tankerCost: billedMonthTankerCost,
    deposit: waterFund,
    collected: waterCollected,
    outstanding: waterOutstanding,
    balance: waterFundBalance,
    billedMonth: lastBilledMonth, // for display label
  };

  // Paid / pending flats for the billed month (May) — shown on Income & Pending cards
  const waterPayees = waterBillsLastBilled
    .filter(w => w.paid)
    .map(w => ({ flatId: w.flatId, amount: w.billAmount || 0 }));
  const waterNonPayees = waterBillsLastBilled
    .filter(w => !w.paid)
    .map(w => ({ flatId: w.flatId, amount: w.billAmount || 0 }));

  // ── Water FY-to-date ──
  const fyWaterBills = waterBills.filter(w => fyMonths.includes(w.month));
  const fyWaterCollected   = fyWaterBills.filter(w => w.paid).reduce((s, w) => s + (w.billAmount || 0), 0);
  const fyWaterOutstanding = fyWaterBills.filter(w => !w.paid).reduce((s, w) => s + (w.billAmount || 0), 0);
  const fyTankers     = tankers.filter(t => fyMonths.includes(t.month));
  const fyTankerCount = fyTankers.reduce((s, t) => s + (t.count || 0), 0);
  const fyTankerCost  = fyTankers.reduce((s, t) => s + ((t.count || 0) * (t.rate || 0)), 0);
  const waterFY = {
    tankerCount: fyTankerCount,
    tankerCost: fyTankerCost,
    deposit: waterFund,
    collected: fyWaterCollected,
    outstanding: fyWaterOutstanding,
    balance: waterFund - fyTankerCost + fyWaterCollected,
  };

  // ── Non-payee flats ──
  const maintenanceNonPayees = outstandingFlats.map(f => ({
    flatId: f.flatId, amount: f.maintenanceRate || 1500,
  }));
  // waterNonPayees already computed above from lastBilledMonth (May)

  return (
    <div>
      {/* This Month's Summary */}
      <SectionHeader title="This Month's Summary" sub={formatMonth(month)} />
      <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "8px", fontWeight: "600" }}>🏠 Maintenance</div>
      <MaintenanceSummaryGrid
        data={maintenanceThisMonth}
        paidFlats={paidFlats}
        pendingFlats={pendingFlats}
      />
      <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "8px", fontWeight: "600" }}>💧 Water &amp; Tanker · {formatMonth(waterThisMonth.billedMonth || month)}</div>
      <WaterSummaryGrid data={waterThisMonth} paidFlats={waterPayees} pendingFlats={waterNonPayees} />

      {/* FY-to-Date Summary */}
      <SectionHeader title="Financial Year to Date" sub={fyToDateLabel()} />
      <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "8px", fontWeight: "600" }}>🏠 Maintenance</div>
      <MaintenanceSummaryGrid data={maintenanceFY} paidFlats={[]} pendingFlats={[]} />
      {/* FY Water & Tanker section hidden — not needed */}

      {/* Tanker chip */}
      {tankerCount > 0 && (
        <div style={{
          background: "#42a5f510", border: "1px solid #42a5f530", borderRadius: "10px",
          padding: "10px 14px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: "12px", color: "#42a5f5", fontWeight: "600" }}>💧 Tanker Summary</div>
            <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{tankerCount} tanker{tankerCount > 1 ? "s" : ""} this month</div>
          </div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#42a5f5" }}>{fmt(tankerCost)}</div>
        </div>
      )}

      {/* My Dues — MC member's own flat */}
      {user?.flatId && (
        <MyDuesSection user={user} flats={flats} payments={payments} maintenanceBills={maintenanceBills} waterBills={waterBills} settings={settings} month={month} activeTenant={activeTenant} onRefresh={onRefresh} />
      )}

      {/* Non-Payee Flats — combined table (Flat No | Maintenance | Water | Total) */}
      <SectionHeader title="Non-Payee Flats" sub="Per-flat outstanding — Maintenance & Water" />
      <NonPayeeTable
        maintenanceNonPayees={maintenanceNonPayees}
        waterNonPayees={waterNonPayees}
      />

      {/* Pending Confirmations */}
      {pendingConfirm.length > 0 && (
        <>
          <SectionHeader
            title="Pending Confirmations"
            sub={`${pendingConfirm.length} declaration${pendingConfirm.length > 1 ? "s" : ""} awaiting MC`}
          />
          {pendingConfirm.map(p => (
            <div key={p.id} style={{
              background: "#ffa72610", border: "1px solid #ffa72630", borderRadius: "12px",
              padding: "12px 14px", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                  Flat {p.flatId}
                </div>
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                  {p.type} · {p.declaredBy || "Resident"}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#ffa726" }}>{fmt(p.amount)}</div>
                <Badge text="Awaiting MC" color="amber" />
              </div>
            </div>
          ))}
          <div style={{ marginBottom: "16px" }} />
        </>
      )}

      {/* ── Society Feedback — MC only ── */}
      {(() => {
        const currMonth = currentMonthKey();
        const thisMonthFb = societyFeedback.filter(f => {
          if (!f.submittedAt) return false;
          const d = f.submittedAt.toDate ? f.submittedAt.toDate() : new Date(f.submittedAt);
          const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
          return key === currMonth;
        });
        const displayFb = fbShowAll ? societyFeedback : thisMonthFb;

        function fbStars(n) { return "★".repeat(n||0) + "☆".repeat(5-(n||0)); }
        function fbDate(ts) {
          if (!ts) return "";
          const d = ts.toDate ? ts.toDate() : new Date(ts);
          return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
        }

        return (
          <div style={{ marginTop: "8px", marginBottom: "16px" }}>
            {/* Collapsible header */}
            <div
              onClick={() => setFbOpen(o => !o)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: fbOpen ? "12px 12px 0 0" : "12px",
                padding: "12px 14px", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "14px" }}>📋</span>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>
                    Society Feedback
                  </div>
                  <div style={{ fontSize: "10px", color: "#555570", marginTop: "1px" }}>
                    {fbLoading ? "Loading…" : `${thisMonthFb.length} this month · ${societyFeedback.length} total`}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {thisMonthFb.length > 0 && (
                  <span style={{
                    background: "#6c63ff22", border: "1px solid #6c63ff40",
                    color: "#6c63ff", borderRadius: "10px", padding: "1px 8px",
                    fontSize: "10px", fontWeight: "700",
                  }}>
                    {thisMonthFb.length} new
                  </span>
                )}
                <span style={{ color: "#555570", fontSize: "12px" }}>{fbOpen ? "▲" : "▼"}</span>
              </div>
            </div>

            {/* Expanded content */}
            {fbOpen && (
              <div style={{
                background: "#13131c", border: "1px solid #2e2e40", borderTop: "none",
                borderRadius: "0 0 12px 12px", padding: "12px 14px",
              }}>
                {fbLoading ? (
                  <div style={{ color: "#555570", fontSize: "12px", textAlign: "center", padding: "12px" }}>
                    Loading feedback…
                  </div>
                ) : displayFb.length === 0 ? (
                  <div style={{ color: "#555570", fontSize: "12px", textAlign: "center", padding: "12px" }}>
                    {fbShowAll ? "No society feedback yet." : "No society feedback this month."}
                  </div>
                ) : (
                  displayFb.map(f => (
                    <div key={f.id} style={{
                      background: "#1a1a24", border: "1px solid #2e2e40",
                      borderRadius: "10px", padding: "10px 12px", marginBottom: "8px",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#6c63ff" }}>{f.screen}</span>
                        <span style={{
                          fontSize: "13px",
                          color: (f.rating||0) >= 4 ? "#4caf50" : (f.rating||0) === 3 ? "#ffa726" : "#ff5252",
                        }}>
                          {fbStars(f.rating)}
                        </span>
                      </div>
                      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "6px" }}>
                        {f.userName} · {f.role}{f.flatId ? ` · Flat ${f.flatId}` : ""} · {fbDate(f.submittedAt)}
                      </div>
                      {f.comment ? (
                        <>
                          <div style={{ fontSize: "12px", color: "#c8c8e0", lineHeight: "1.5", marginBottom: "4px" }}>
                            "{f.comment}"
                          </div>
                          <div style={{ fontSize: "10px", color: "#444460", fontStyle: "italic" }}>
                            💡 If in another language, copy and paste into{" "}
                            <a href="https://translate.google.com" target="_blank" rel="noreferrer"
                              style={{ color: "#6c63ff" }}>translate.google.com</a>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: "11px", color: "#444460", fontStyle: "italic" }}>No comment</div>
                      )}
                    </div>
                  ))
                )}

                {/* Show all / Show this month toggle */}
                {!fbLoading && societyFeedback.length > 0 && (
                  <div style={{ textAlign: "center", marginTop: "4px" }}>
                    <button
                      onClick={() => setFbShowAll(v => !v)}
                      style={{
                        background: "none", border: "none", color: "#6c63ff",
                        fontSize: "12px", cursor: "pointer", textDecoration: "underline",
                      }}
                    >
                      {fbShowAll
                        ? `Show this month only (${thisMonthFb.length})`
                        : `Show all feedback (${societyFeedback.length})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// OWNER / TENANT / FAMILY DASHBOARD VIEW
// ══════════════════════════════════════════════════════════════
function ResidentDashboard({ user, settings, flats, payments, maintenanceBills, waterBills, activeTenant, onRefresh }) {
  const month = currentMonthKey();

  // Non-payee flats — society-wide (flat numbers + amount only)
  const confirmedThisMonth = payments.filter(
    p => p.month === month && p.status === "confirmed" &&
    (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
  );
  const paidFlatIds = new Set(confirmedThisMonth.map(p => p.flatId));
  const maintenanceNonPayees = flats
    .filter(f => !paidFlatIds.has(f.flatId))
    .map(f => ({ flatId: f.flatId, amount: f.maintenanceRate || 1500 }));
  const waterNonPayees = waterBills
    .filter(w => {
      // Billing lag: collect previous month's bills this month
      const fyMonths = fyMonthKeys();
      const lastBilled = fyMonths.length >= 2 ? fyMonths[fyMonths.length - 2] : fyMonths[0];
      return w.month === lastBilled && !w.paid;
    })
    .map(w => ({ flatId: w.flatId, amount: w.billAmount || 0 }));

  return (
    <div>
      {/* My Dues — Maintenance & Water ledger + status banner */}
      <MyDuesSection user={user} flats={flats} payments={payments} maintenanceBills={maintenanceBills} waterBills={waterBills} settings={settings} month={month} activeTenant={activeTenant} onRefresh={onRefresh} />

      {/* Non-Payee Flats */}
      <SectionHeader title="Non-Payee Flats" sub="Flat numbers & total outstanding" />
      <NonPayeeSummary
        maintenanceNonPayees={maintenanceNonPayees}
        waterNonPayees={waterNonPayees}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ══════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user, logout, isPresident, isMC } = useAuth();
  const [activeTab,    setActiveTab]    = useState(user?.role === "Watchman" ? "water" : "dashboard");

  // Data state
  const [settings,      setSettings]      = useState(null);
  const [flats,         setFlats]         = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [expenses,      setExpenses]      = useState([]);
  const [maintenanceIncome, setMaintenanceIncome] = useState([]);
  const [waterBills,    setWaterBills]    = useState([]);
  const [tankers,       setTankers]       = useState([]);
  const [maintenanceBills, setMaintenanceBills] = useState([]);
  const [dataLoading,      setDataLoading]      = useState(true);
  const [duesPopupData,    setDuesPopupData]    = useState([]);
  const [activeTenant,     setActiveTenant]     = useState(null); // active tenant in same flat, if any
  const [showTankerFloat,  setShowTankerFloat]  = useState(false);
  const [appVersion,       setAppVersion]       = useState("v1.0.0"); // fallback until Firestore loads

  const month = currentMonthKey();
  const fyMonths = fyMonthKeys();

  async function loadData() {
    try {
      // Settings
      const settingsSnap = await getDoc(doc(db, "settings", "init"));
      const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
      if (settingsData) setSettings(settingsData);

      // Tracking Start Month for this society (e.g. "2026-04" for BSKR).
      // Falls back to "2026-04" only if it's ever missing from Settings.
      const trackingStart = (settingsData && settingsData.trackingStartMonth) || "2026-04";

      // Flats
      const flatsSnap = await getDocs(
        query(collection(db, "flats"), where("societyId", "==", user?.societyId))
      );
      const flatsData = flatsSnap.docs.map(d => ({ flatId: d.id, ...d.data() }));
      // Sort: G1, G2, G3, 101, 102...
      flatsData.sort((a, b) => {
        const order = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];
        return order.indexOf(a.flatId) - order.indexOf(b.flatId);
      });
      setFlats(flatsData);

      // Maintenance bills — the saved, per-month record for every flat
      // (each keeps its own locked-in rate and Occupied/Vacant status).
      const maintenanceBillsSnap = await getDocs(
        query(
          collection(db, "maintenanceBills"),
          where("societyId", "==", user?.societyId),
          where("month", ">=", trackingStart)
        )
      );
      setMaintenanceBills(maintenanceBillsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Payments — everything since this society's Tracking Start Month,
      // not just the current financial year. This is the fix: a plain
      // "month >= trackingStart" range never loses older confirmed
      // payments once a new financial year begins (was the root cause
      // of the Brought Forward bug — Total Paid would have quietly
      // undercounted starting every April).
      const paymentsSnap = await getDocs(
        query(
          collection(db, "payments"),
          where("societyId", "==", user?.societyId),
          where("month", ">=", trackingStart)
        )
      );
      setPayments(paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Expenses — FY-to-date (Apr through current month)
      const expensesSnap = await getDocs(
        query(
          collection(db, "expenses"),
          where("societyId", "==", user?.societyId),
          where("month", "in", fyMonths)
        )
      );
      setExpenses(expensesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Maintenance misc income — FY-to-date (bank interest, scrap, donations etc.)
      const maintIncomeSnap = await getDocs(
        query(
          collection(db, "maintenanceIncome"),
          where("societyId", "==", user?.societyId),
          where("month", "in", fyMonths)
        )
      );
      setMaintenanceIncome(maintIncomeSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Water bills — FY-to-date (June will be empty until bills are calculated)
      const waterBillsSnap = await getDocs(
        query(
          collection(db, "waterBills"),
          where("societyId", "==", user?.societyId),
          where("month", "in", fyMonths)
        )
      );
      setWaterBills(waterBillsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Tankers — FY-to-date (Apr through current month)
      const tankerSnap = await getDocs(
        query(
          collection(db, "tankerOrders"),
          where("societyId", "==", user?.societyId),
          where("month", "in", fyMonths)
        )
      );
      setTankers(tankerSnap.docs.map(d => ({ id: d.id, ...d.data() })));


      // Active tenant for this flat (owner is always the logged-in user here)
      if (user?.flatId) {
        const tenantSnap = await getDocs(
          query(
            collection(db, "users"),
            where("societyId", "==", user?.societyId),
            where("flatId",    "==", user.flatId),
            where("memberType","==", "tenant"),
            where("active",    "==", true)
          )
        );
        const tenantDoc = tenantSnap.docs[0];
        setActiveTenant(tenantDoc ? { id: tenantDoc.id, ...tenantDoc.data() } : null);
      }

    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [user?.id || user?.uid, month]);

  // ── App version — read once from Firestore, shown in footer + feedback ──
  useEffect(() => {
    async function loadAppVersion() {
      try {
        const versionSnap = await getDoc(doc(db, "settings", "appVersion"));
        if (versionSnap.exists() && versionSnap.data().version) {
          setAppVersion(versionSnap.data().version);
        }
      } catch (err) {
        console.error("Could not load app version:", err);
        // Falls back to "v1.0.0" already set as the default — safe, no crash.
      }
    }
    loadAppVersion();
  }, []);

  // ── After data loads, calculate pending dues for NotificationBell ──
  useEffect(() => {
    async function calcDues() {
      if (dataLoading) return;
      if (isMC() || user?.role === "Watchman") return;
      if (!user?.flatId) return;

      const pendingDues = [];

      const maintenancePaid = payments.some(
        p => p.flatId === user.flatId && p.month === month &&
             (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both") && p.status === "confirmed"
      );
      const maintenanceDeclared = payments.some(
        p => p.flatId === user.flatId && p.month === month &&
             (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both") && p.status === "declared"
      );
      if (!maintenancePaid && !maintenanceDeclared) {
        const flat = flats.find(f => f.flatId === user.flatId);
        pendingDues.push({
          type: "maintenance",
          label: "Maintenance",
          month,
          amount: flat?.maintenanceRate || settings?.maintenanceAmt || 1500,
        });
      }

      const fyMonths = fyMonthKeys();
      const lastBilledMonth = fyMonths.length >= 2 ? fyMonths[fyMonths.length - 2] : null;
      if (lastBilledMonth) {
        const waterBill = waterBills.find(
          w => w.flatId === user.flatId && w.month === lastBilledMonth && w.lockedAt
        );
        if (waterBill && !waterBill.paid) {
          pendingDues.push({
            type: "water",
            label: "Water Bill",
            month: lastBilledMonth,
            amount: waterBill.totalDue || waterBill.billAmount || 0,
          });
        }
      }

      // ── Society Work (Adhoc) pending dues ──────────────────────
      try {
        const adhocSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("flatId", "==", user.flatId)
        ));
        const adhocPending = adhocSnap.docs
          .map(d => ({ docId: d.id, ...d.data() }))
          .filter(d => d.paid !== true && d.status !== "confirmed");

        for (const adhoc of adhocPending) {
          pendingDues.push({
            type: "adhoc",
            label: `🏗️ ${adhoc.eventTitle || "Society Work"}`,
            month: null,
            amount: adhoc.amount || 0,
          });
        }
      } catch (e) {
        console.error("Failed to load adhoc dues for notification:", e);
      }

      setDuesPopupData(pendingDues);
    }
    calcDues();
  }, [dataLoading]);


  const visibleTabs = user?.role === "Watchman"
    ? TABS.filter(t => t.id === "water" || t.id === "contacts")
    : TABS.filter(t => (!t.mcOnly || isMC()) && (!t.presidentOnly || isPresident()));

  // Role label colour
  const roleColor = isPresident()
    ? "#6c63ff" : isMC()
    ? "#42a5f5" : "#4caf50";

  return (
    <div style={{ background: "#0f0f13", minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* ── Sticky Header ── */}
      <div style={{
        background: "#1a1a24", borderBottom: "1px solid #2e2e40",
        padding: "10px 16px", position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {/* Left — Adyantam brand */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={adyantamLogo} alt="Adyantam Building Circle"
            style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }} />
          <div>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#6c63ff", lineHeight: 1.2 }}>
              Adyantam Building Circle®
            </div>
            <div style={{ fontSize: "10px", color: "#555570" }}>
              {settings?.societyName || "Society"}
            </div>
          </div>
        </div>

        {/* Right — bell + user chip + logout */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

          {/* 🏛️ Society ID badge — President only */}
          {isPresident() && settings?.societyId && (
            <span style={{
              fontSize: "11px", fontWeight: "600", color: "#4caf50",
              background: "#4caf5018", border: "1px solid #4caf5040",
              borderRadius: "8px", padding: "4px 10px",
              display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap",
            }}>
              🏛️ {settings.societyId}
            </span>
          )}

          {/* 🔔 Notification Bell */}
          {user?.role !== "Watchman" && (
            <NotificationBell user={user} duesData={duesPopupData} />
          )}

          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#e8e8f0" }}>{user.name}</div>
            <div style={{ fontSize: "10px", color: roleColor }}>{user.role}{user.flatId ? ` · ${user.flatId}` : ""}</div>
          </div>
          <button
            onClick={logout}
            style={{
              background: "#ff525220", border: "1px solid #ff525240", color: "#ff5252",
              borderRadius: "8px", padding: "6px 10px", fontSize: "11px",
              fontWeight: "600", cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>



      {/* ── Tab Bar ── */}
      <div style={{
        background: "#1a1a24", borderBottom: "1px solid #2e2e40",
        position: "sticky", top: "53px", zIndex: 99,
        overflowX: "auto", display: "flex", padding: "0 8px",
        scrollbarWidth: "none",
      }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flexShrink: 0, padding: "10px 12px", fontSize: "12px", fontWeight: "500",
              color: activeTab === tab.id ? "#6c63ff" : "#8888a8",
              background: "none", border: "none", cursor: "pointer",
              borderBottom: activeTab === tab.id ? "2px solid #6c63ff" : "2px solid transparent",
              whiteSpace: "nowrap", transition: "all 0.2s",
            }}
          >
            <span style={{ marginRight: "4px" }}>{tab.id === "water" && user?.role === "Watchman" ? "🧮" : tab.icon}</span>
            {tab.id === "water" && user?.role === "Watchman" ? "Meter" : tab.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "14px", maxWidth: "640px", margin: "0 auto" }}>

        {/* Loading */}
        {dataLoading && activeTab === "dashboard" && (
          <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
            Loading dashboard…
          </div>
        )}

        {/* Dashboard tab */}
        {!dataLoading && activeTab === "dashboard" && (
          isMC()
            ? <MCDashboard
                settings={settings}
                flats={flats}
                payments={payments}
                maintenanceBills={maintenanceBills}
                expenses={expenses}
                maintenanceIncome={maintenanceIncome}
                waterBills={waterBills}
                tankers={tankers}
                user={user}
                activeTenant={activeTenant}
                onRefresh={loadData}
              />
            : <ResidentDashboard
                user={user}
                settings={settings}
                flats={flats}
                payments={payments}
                maintenanceBills={maintenanceBills}
                waterBills={waterBills}
                activeTenant={activeTenant}
                onRefresh={loadData}
              />
        )}

        {/* Flats tab */}
        {activeTab === "flats" && <FlatsScreen />}

        {/* Wired tabs */}
        {activeTab === "finance" && <PassbookScreen />}
        {activeTab === "water" && (
          <WaterScreen
            canEdit={isMC() || user?.role === "Watchman"}
            user={user}
            isPresident={isPresident}
          />
        )}
        {activeTab === "poll" && <PollScreen />}
        {activeTab === "tasks" && <TasksScreen />}
        {activeTab === "chat" && <ChatScreen />}
        {activeTab === "contacts" && <ContactsScreen />}
        {activeTab === "bylaws" && <BylawsScreen />}
        {activeTab === "reports" && <ReportsScreen />}

        {/* Other tabs — coming soon */}
        {activeTab !== "dashboard" && activeTab !== "settings" && activeTab !== "flats" &&
         activeTab !== "finance" && activeTab !== "water" &&
         activeTab !== "poll" && activeTab !== "tasks" && activeTab !== "chat" &&
         activeTab !== "contacts" && activeTab !== "bylaws" && activeTab !== "reports" && (
          <ComingSoon tab={activeTab} />
        )}

        {/* Settings tab */}
        {/* activeTab === "tankerreset" && isPresident() && (
          <WaterDataImportScreen />
        )*/}
        {activeTab === "settings" && isMC() && (
          <SettingsScreen />
        )}
        {/* ── Floating Tanker Order Button — MC only ── */}
        {isMC() && user?.role !== "Watchman" && (
          <div style={{ position: "fixed", bottom: "132px", right: "16px", zIndex: 200 }}>
            <button
              onClick={() => setShowTankerFloat(true)}
              title="Quick Tanker Order"
              style={{
                width: "52px", height: "52px", borderRadius: "50%",
                background: "#1a1a24", border: "2px solid #42a5f5",
                color: "#42a5f5", fontSize: "22px",
                cursor: "pointer", boxShadow: "0 4px 16px #42a5f530",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              🚛
            </button>
          </div>
        )}

        {showTankerFloat && (
          <TankerOrderModal
            user={user}
            settings={settings}
            onClose={() => setShowTankerFloat(false)}
            onDone={() => { setShowTankerFloat(false); loadData(); }}
          />
        )}

        {/* ── Feedback Widget — shown on all tabs except Watchman ── */}
        {user?.role !== "Watchman" && (
          <FeedbackWidget
            screenName={TABS.find(t => t.id === activeTab)?.label || activeTab}
            user={user}
            appVersion={appVersion}
          />
        )}

        {/* ── App Footer ── */}
        <div style={{
          marginTop: "32px",
          borderTop: "1px solid #6c63ff40",
          textAlign: "center",
          background: "#12121a",
          borderRadius: "0 0 12px 12px",
          padding: "20px 16px",
        }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: "#6c63ff", marginBottom: "4px", letterSpacing: "0.3px" }}>
            Adyantam Building Circle®
          </div>
          <div style={{ fontSize: "11px", color: "#6c63ff80", marginBottom: "12px", letterSpacing: "0.5px" }}>
            Empowering Resident Communities
          </div>
          <div style={{ width: "40px", height: "1px", background: "#2e2e40", margin: "0 auto 12px" }} />
          <div style={{ fontSize: "12px", color: "#a0a0c0", marginBottom: "10px" }}>
            © {new Date().getFullYear()} Adyantam. All rights reserved. · {appVersion}
          </div>
          <div style={{ fontSize: "12px", fontStyle: "italic", color: "#ffa726", lineHeight: "1.6" }}>
            you don't need to know code to build —{" "}
            <span style={{ fontWeight: "700", color: "#ffb74d" }}>
              Conceptualised by Dr. Vijay DSK, powered by AI
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
